import * as React from 'react'

type PollConfig = {
  pollTime?: number
  retryInterval?: number
  maxRetries?: number
  onFailure?: (error: any) => void
}

type StartConfig = {
  /**
   * Whether to run the callback right away. Defaults to true. Pass false when
   * the caller already holds fresh data and only wants the loop armed for what
   * comes next.
   */
  immediate?: boolean
}

type Poller = { start: (config?: StartConfig) => void; stop: () => void }

export function usePoll(
  callback: () => Promise<void>,
  { pollTime = 10000, retryInterval = 1000, maxRetries = 3, onFailure = () => {} }: PollConfig = {},
): Poller {
  const retryCountRef = React.useRef(0)
  const timeoutHandle = React.useRef<number | undefined>(undefined)

  /**
   * The loop re-arms itself from inside a timeout, so it would otherwise run
   * forever on the callback and the config of the render that started it —
   * `start` is typically called once from a mount effect, which means the very
   * first ones. A callback closing over state or props would keep comparing
   * against values from that first render.
   *
   * Read through a ref instead, refreshed on every render, so each tick calls
   * the current callback with the current config.
   */
  const latestRef = React.useRef({ callback, pollTime, retryInterval, maxRetries, onFailure })
  latestRef.current = { callback, pollTime, retryInterval, maxRetries, onFailure }

  /**
   * Bumped on every stop, and carried by each scheduled call. A call that was
   * already in flight when the caller stopped compares it and gives up instead
   * of re-arming — without it, clearing the timeout is not enough to stop the
   * loop, since a pending callback schedules the next tick as it resolves.
   */
  const generationRef = React.useRef(0)

  /**
   * Stable across renders, so callers can list the poller in a dependency array
   * without restarting the loop on every render. `start` and `stop` read
   * everything they need through refs, so the pair captured on the first render
   * stays correct for the life of the hook — which is what makes this safe.
   *
   * Filled in on first use rather than passed to `useRef`, whose argument is
   * evaluated on every render only for the result to be thrown away.
   */
  const pollerRef = React.useRef<Poller | undefined>(undefined)

  if (pollerRef.current === undefined) {
    pollerRef.current = { start, stop }
  }

  return pollerRef.current

  function start({ immediate = true }: StartConfig = {}): void {
    stop()

    // A fresh start gets a fresh retry budget: without this, a loop that had
    // already exhausted its retries and reported would give up on the first
    // failure of every later start.
    retryCountRef.current = 0

    if (immediate) {
      void poll(generationRef.current)
      return
    }

    schedule(generationRef.current, latestRef.current.pollTime)
  }

  function stop(): void {
    generationRef.current += 1
    window.clearTimeout(timeoutHandle.current)
    timeoutHandle.current = undefined
  }

  function schedule(generation: number, delay: number): void {
    timeoutHandle.current = window.setTimeout(() => void poll(generation), delay)
  }

  async function poll(generation: number): Promise<void> {
    try {
      await latestRef.current.callback()

      if (generation !== generationRef.current) {
        return
      }

      retryCountRef.current = 0
      schedule(generation, latestRef.current.pollTime)
    } catch (error) {
      if (generation !== generationRef.current) {
        return
      }

      if (retryCountRef.current < latestRef.current.maxRetries) {
        retryCountRef.current += 1
        schedule(generation, latestRef.current.retryInterval)
        return
      }

      latestRef.current.onFailure(error)
    }
  }
}
